/**
 * `staleTime` e `gcTime` **derivados do `Cache-Control` que a propria API declara**.
 *
 * O briefing pede que as chaves sejam "coerentes com a tabela de cache do `PWA.md`" e que "o
 * que a API marca `no-store` nao fique em cache de query com `staleTime` longo". A forma
 * frágil de fazer isso e uma tabela de numeros escritos a mao. A forma que nao apodrece e esta:
 * cada familia aponta para a **constante de `Cache-Control` que `@farmacia/contracts` ja
 * exporta** (`CATALOG_AVAILABILITY_CACHE_CONTROL`, `CART_CACHE_CONTROL`, ...), e o `staleTime`
 * sai do `s-maxage` daquela string.
 *
 * A consequencia e a que importa: no dia em que alguem trocar um `public, s-maxage=60` por
 * `no-store` no contrato, o `staleTime` desta familia vira zero **no mesmo commit**, sem que
 * ninguem precise lembrar deste arquivo. E o teste `cache-policy.test.ts` reprova qualquer
 * familia `no-store` que tenha `staleTime > 0` — a protecao falha quando e removida.
 *
 * ## Por que `gcTime: 0` no que e `no-store`
 *
 * `staleTime: 0` faz o TanStack Query **revalidar**, mas ele continua guardando a ultima
 * resposta em memoria para exibir enquanto revalida. Para preco, estoque, carrinho, cotacao,
 * pedido e fila do Painel isso e exatamente o que `PWA §4.2 R11/R12` proibe: mostrar preco
 * vencido, ainda que por um instante, e oferta que vincula (CDC art. 30). `gcTime: 0` descarta
 * a entrada assim que ninguem a observa.
 */
import {
  ADDRESS_CACHE_CONTROL,
  AUTH_CACHE_CONTROL,
  CART_CACHE_CONTROL,
  CATALOG_AVAILABILITY_CACHE_CONTROL,
  CATALOG_CATEGORIES_CACHE_CONTROL,
  CATALOG_PERSONALIZED_CACHE_CONTROL,
  CATALOG_PRODUCTS_CACHE_CONTROL,
  CHECKOUT_QUOTE_CACHE_CONTROL,
  DELIVERY_ZONES_CACHE_CONTROL,
  ORDER_CACHE_CONTROL,
  PUBLIC_STORES_CACHE_CONTROL,
  SEARCH_CACHE_CONTROL,
  STORE_COUNTER_CACHE_CONTROL,
  STORE_QUEUE_CACHE_CONTROL,
} from '@farmacia/contracts';

/** As familias de query do MVP. Uma por linha da tabela do `API §8.1` que o cliente consome. */
export type QueryFamily =
  | 'public.stores'
  | 'public.zones'
  | 'public.categories'
  | 'catalog.products'
  | 'catalog.product'
  | 'catalog.availability'
  | 'catalog.search'
  | 'cart'
  | 'checkout.quote'
  | 'orders'
  /** `GET /v1/me/addresses` — `API §6.2`, X-05: autenticado e sempre `no-store`. */
  | 'me.addresses'
  | 'store.queue'
  | 'store.counter'
  | 'auth';

export interface CachePolicy {
  /** Quanto tempo o dado e considerado fresco. `0` em tudo que a API marca `no-store`. */
  readonly staleTime: number;
  /** Quanto tempo a entrada sobrevive sem observador. `0` no que e `no-store`. */
  readonly gcTime: number;
  /** A resposta pode ir para o IndexedDB (`PWA §7.3`)? `no-store` nunca pode. */
  readonly persistable: boolean;
  /** A linha do contrato de onde os dois numeros sairam. */
  readonly cacheControl: string;
}

/** O `Cache-Control` de cada familia — **importado**, nunca redigitado. */
export const FAMILY_CACHE_CONTROL: Readonly<Record<QueryFamily, string>> = {
  'public.stores': PUBLIC_STORES_CACHE_CONTROL,
  'public.zones': DELIVERY_ZONES_CACHE_CONTROL,
  'public.categories': CATALOG_CATEGORIES_CACHE_CONTROL,
  'catalog.products': CATALOG_PRODUCTS_CACHE_CONTROL,
  'catalog.product': CATALOG_PRODUCTS_CACHE_CONTROL,
  'catalog.availability': CATALOG_AVAILABILITY_CACHE_CONTROL,
  'catalog.search': SEARCH_CACHE_CONTROL,
  cart: CART_CACHE_CONTROL,
  'checkout.quote': CHECKOUT_QUOTE_CACHE_CONTROL,
  orders: ORDER_CACHE_CONTROL,
  'me.addresses': ADDRESS_CACHE_CONTROL,
  'store.queue': STORE_QUEUE_CACHE_CONTROL,
  'store.counter': STORE_COUNTER_CACHE_CONTROL,
  auth: AUTH_CACHE_CONTROL,
};

/** `no-store` **ou** `private` — as duas formas em que a tabela do `API §8.1` diz "nao guarde". */
export function isNoStore(cacheControl: string): boolean {
  return /no-store/i.test(cacheControl);
}

/** `s-maxage=600` -> `600`. `null` quando a diretiva nao esta na string. */
export function directiveSeconds(cacheControl: string, directive: string): number | null {
  const match = new RegExp(`(?:^|[,\\s])${directive}\\s*=\\s*(\\d+)`, 'i').exec(cacheControl);
  if (match === null) return null;
  const raw = match[1];
  return raw === undefined ? null : Number(raw);
}

/**
 * A politica de uma familia.
 *
 * `staleTime` sai do `s-maxage` (a borda), e nao do `max-age` (o navegador): a tabela do
 * `API §8.1` usa `max-age=0, s-maxage=600` nas rotas de allowlist do service worker — o
 * navegador revalida sempre, a borda guarda 10 min. Para o TanStack Query, que fala com a
 * borda, o numero util e o da borda.
 *
 * `gcTime` sai do `stale-while-revalidate`: e o tempo que o contrato admite servir uma resposta
 * velha enquanto busca a nova, que e exatamente a semantica de "manter na memoria sem
 * observador". Sem `swr` declarado, cai no `s-maxage`.
 */
export function cachePolicyFor(family: QueryFamily): CachePolicy {
  const cacheControl = FAMILY_CACHE_CONTROL[family];

  if (isNoStore(cacheControl)) {
    return { staleTime: 0, gcTime: 0, persistable: false, cacheControl };
  }

  const sharedMaxAge = directiveSeconds(cacheControl, 's-maxage') ?? 0;
  const swr = directiveSeconds(cacheControl, 'stale-while-revalidate') ?? sharedMaxAge;

  return {
    staleTime: sharedMaxAge * 1000,
    gcTime: swr * 1000,
    persistable: true,
    cacheControl,
  };
}

/**
 * A politica de catalogo **depende de haver sessao**, e ignorar isso seria um vazamento.
 *
 * `API §8.1` tem duas linhas para a mesma rota:
 *
 * | Situacao | `Cache-Control` |
 * | -------- | --------------- |
 * | `GET /v1/catalog/products` anonimo | `public, s-maxage=60, swr=300` |
 * | qualquer catalogo **com preco de clube/beneficio** | `private, no-store` |
 *
 * A segunda linha traz a justificativa junto: *"Preco personalizado em CDN e vazamento e preco
 * errado para o proximo visitante"*. Num aparelho compartilhado em familia — a norma no
 * publico-alvo (`PWA §4.1`) — guardar 5 minutos do preco de clube de quem estava logado antes e
 * a mesma falha dentro do navegador.
 *
 * Por isso a familia de catalogo nao tem uma politica so: com sessao, ela e `no-store`.
 */
export function catalogCachePolicy(hasSession: boolean): CachePolicy {
  if (!hasSession) return cachePolicyFor('catalog.products');
  return {
    staleTime: 0,
    gcTime: 0,
    persistable: false,
    cacheControl: CATALOG_PERSONALIZED_CACHE_CONTROL,
  };
}

/**
 * Intervalo de polling da fila do Painel: **5 s** (ADR-0013, "fila por polling de 5 s").
 *
 * Mora aqui e nao na tela porque `API §9` item 6 manda o cliente **dobrar** este intervalo
 * quando o orcamento de rate limit fica abaixo de 10% — e quem ve o header `RateLimit` e o
 * cliente HTTP, nao o componente.
 */
export const STORE_QUEUE_POLL_INTERVAL_MS = 5_000;
